import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { FSDocument, FSDocumentElement } from '@fuse/components/fullscreen/fullscreen.types';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AcepDossier } from 'app/shared/models/acep/acep-dossier.model';
import { Actor } from 'app/shared/models/actors-users/actor.model';
import { ExternalAuditBatch } from 'app/shared/models/audits/external-audit/external-audit-batch.model';
import { RenovationProjectBeforeRenovation } from 'app/shared/models/renovation-projects/renovation-project-before-renovation.model';
import { AcepDossierService } from 'app/shared/services/acep/acep-dossier.service';
import { ActorService } from 'app/shared/services/actors-users/actor.service';
import { RouterNavigationService } from 'app/shared/classes/router-navigation.service';
import { ExternalAuditBatchService } from 'app/shared/services/audits/external-audit/external-audit-batch.service';
import { RenovationProjectBeforeRenovationService } from 'app/shared/services/renovation-projects/renovation-project-before-renovation.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { ActorTypes } from 'app/shared/enumerations/actor-types.enum';
import { BatchStatusKey } from 'app/shared/enumerations/statuses.enum';
import { environment } from 'environments/environment';

@Component({
  selector: 'fuse-fullscreen',
  templateUrl: './fullscreen.component.html',
  styleUrls: ['./fullscreen.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'fuseFullscreen'
})
export class FuseFullscreenComponent implements OnInit, AfterViewInit {
  readonly searchResultsPerCategoryLimit: number = 6;
  readonly categoryOrder: SearchRecordType[] = ['projects', 'actors', 'dossiers', 'lots-css'];
  @Input() iconTpl: TemplateRef<any>;
  @Input() tooltip: string;

  searchQuery: string = '';
  groupedResults: SearchResultGroup[] = [];
  searchPanelWidth: string = '100%';
  isSearchDataLoading: boolean = false;
  displayedCounts: Record<string, number> = {};

  @ViewChild('searchInputWrap') searchInputWrapRef: ElementRef<HTMLElement>;
  @ViewChild('globalSearchInput') searchInputRef: ElementRef<HTMLInputElement>;
  isSearchFocused: boolean = false;

  private _searchRecords: SearchRecord[] = [];
  private _hasSearchDataLoaded: boolean = false;

  private _fsDoc: FSDocument;
  private _fsDocEl: FSDocumentElement;
  private _isFullscreen: boolean = false;

  /**
   * Constructor
   */
  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private readonly _acepDossierService: AcepDossierService,
    private readonly _projectService: RenovationProjectBeforeRenovationService,
    private readonly _externalAuditBatchService: ExternalAuditBatchService,
    private readonly _actorService: ActorService,
    private readonly _navigationService: NavigationService,
    private readonly _routerNavService: RouterNavigationService
  ) {
    this._fsDoc = _document as FSDocument;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this._fsDocEl = document.documentElement as FSDocumentElement;
  }

  ngAfterViewInit(): void {
    this._updateSearchPanelWidth();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this._updateSearchPanelWidth();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle the fullscreen mode
   */
  toggleFullscreen(): void {
    // Check if the fullscreen is open
    this._isFullscreen = this._getBrowserFullscreenElement() !== null;

    // Toggle the fullscreen
    if (this._isFullscreen) {
      this._closeFullscreen();
    } else {
      this._openFullscreen();
    }
  }

  /**
   * Filter the global records using a single query across all sections
   */
  onSearch(query: string): void {
    this.searchQuery = query;
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) {
      this.groupedResults = [];
      return;
    }

    if (!this._hasSearchDataLoaded) {
      if (!this.isSearchDataLoading) {
        this._loadSearchData();
      }
      return;
    }

    const groupedMap = new Map<SearchRecordType, SearchRecord[]>();
    this.categoryOrder.forEach(type => groupedMap.set(type, []));

    this._searchRecords.forEach(record => {
      const searchable =
        `${record.id} ${record.title} ${record.subtitle} ${record.type}`.toLowerCase();
      if (!searchable.includes(normalizedQuery)) {
        return;
      }

      const list = groupedMap.get(record.type);
      if (!list) {
        return;
      }

      list.push(record);
    });

    this.groupedResults = this.categoryOrder
      .map(type => ({
        id: type,
        results: groupedMap.get(type) || []
      }))
      .filter(group => group.results.length > 0);

    // Initialize displayed counts per group (show first N items)
    this.displayedCounts = {};
    this.groupedResults.forEach(group => {
      this.displayedCounts[group.id] = Math.min(
        group.results.length,
        this.searchResultsPerCategoryLimit
      );
    });
  }

  showMore(groupId: SearchRecordType): void {
    const current = this.displayedCounts[groupId] || 0;
    const group = this.groupedResults.find(g => g.id === groupId);
    const total = group ? group.results.length : 0;
    this.displayedCounts[groupId] = Math.min(
      current + this.searchResultsPerCategoryLimit,
      total
    );
  }

  @HostListener('window:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    // Detect Ctrl+F (or Command+F on Mac)
    const key = (event.key || '').toLowerCase();
    if ((event.ctrlKey || event.metaKey) && key === 'f') {
      event.preventDefault();
      this.focusSearch();
    }
  }

  onSearchFocus(): void {
    this.isSearchFocused = true;
  }

  onSearchBlur(): void {
    this.isSearchFocused = false;
  }

  focusSearch(): void {
    // Ensure we focus the native input element
    this.searchInputRef?.nativeElement?.focus();
  }

  displaySearchResult = (value: string): string => value || '';

  trackByResultId(_: number, record: SearchRecord): string {
    return `${record.type}-${record.entityId}`;
  }

  trackByGroupId(_: number, group: SearchResultGroup): string {
    return group.id;
  }

  get hasGroupedResults(): boolean {
    return this.groupedResults.length > 0;
  }

  getTypeLabel(type: SearchRecordType): string {
    if (type === 'actors') {
      return 'Acteurs';
    }

    if (type === 'projects') {
      return 'Projets';
    }

    if (type === 'dossiers') {
      return 'Dossiers';
    }

    return 'Lots';
  }

  getTypeIcon(type: SearchRecordType): string {
    if (type === 'actors') {
      return 'heroicons_outline:user-group';
    }

    if (type === 'projects') {
      return 'heroicons_outline:briefcase';
    }

    if (type === 'dossiers') {
      return 'heroicons_outline:folder';
    }

    return 'heroicons_outline:collection';
  }

  getResultTooltip(result: SearchRecord): string {
    return `${result.id} - ${result.subtitle}`;
  }

  openResult(result: SearchRecord, event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (result.type === 'dossiers') {
      const typeDossier = 'CEE';
      const idDossier = encodeURIComponent(result.entityId);
      const link = `${environment.ACEP}FicheInfo/FicheInfo?typeDossier=${encodeURIComponent(typeDossier)}&idDossier=${idDossier}`;
      window.open(link, '_blank');
      return;
    }

    if (result.type === 'projects') {
      const navigationLink = this._navigationService.getNavigationLinkById(
        'projet_fiche_informations'
      );
      this._routerNavService.navigateOrOpenInNewTab([navigationLink], {
        event,
        state: {
          renovationProjectId: result.entityId,
          parentDataNavigationId: result.parentDataNavigationId || 'projet_avant_travaux_a_traiter'
        },
        storage: {
          renovationProjectId: result.entityId,
          parentDataNavigationId: result.parentDataNavigationId || 'projet_avant_travaux_a_traiter'
        }
      });
      return;
    }

    if (result.type === 'lots-css') {
      const navigationLink = this._navigationService.getNavigationLinkById('batch_details');
      this._routerNavService.navigateOrOpenInNewTab([navigationLink], {
        event,
        state: { batchId: result.entityId },
        storage: { batchId: result.entityId }
      });
      return;
    }

    const navigationId = this._getActorContactNavigationId(result.actorTypeCode);
    const navigationLink = this._navigationService.getNavigationLinkById(navigationId);
    this._routerNavService.navigateOrOpenInNewTab([navigationLink], {
      event,
      state: { contactsActorId: result.entityId },
      storage: { contactsActorId: result.entityId }
    });
  }

  private _loadSearchData(): void {
    this.isSearchDataLoading = true;

    forkJoin({
      dossiers: this._acepDossierService
        .getCurrentDossiers()
        .pipe(catchError(() => of([] as AcepDossier[]))),
      projectsToDo: this._projectService
        .getAllAvailableForProcessing()
        .pipe(catchError(() => of([] as RenovationProjectBeforeRenovation[]))),
      projectsDone: this._projectService
        .getAllProcessingDone()
        .pipe(catchError(() => of([] as RenovationProjectBeforeRenovation[]))),
      externalAuditBatches: this._externalAuditBatchService
        .getAllByStatus(BatchStatusKey.Tous, null)
        .pipe(catchError(() => of({ batches: [] as ExternalAuditBatch[] }))),
      actors: this._actorService.getAll().pipe(catchError(() => of([] as Actor[])))
    }).subscribe(({ dossiers, projectsToDo, projectsDone, externalAuditBatches, actors }) => {
      const projectsMap = new Map<string, SearchRecord>();

      projectsToDo.forEach(project => {
        projectsMap.set(
          project.id,
          this._toProjectRecord(project, 'projet_avant_travaux_a_traiter')
        );
      });

      projectsDone.forEach(project => {
        projectsMap.set(project.id, this._toProjectRecord(project, 'projet_avant_travaux_traites'));
      });

      this._searchRecords = [
        ...dossiers.map(dossier => this._toDossierRecord(dossier)),
        ...Array.from(projectsMap.values()),
        ...(externalAuditBatches.batches || []).map(batch => this._toLotCssRecord(batch)),
        ...actors.map(actor => this._toActorRecord(actor))
      ];

      this.isSearchDataLoading = false;
      this._hasSearchDataLoaded = true;
      this.onSearch(this.searchQuery);

      if (this.searchQuery.trim()) {
        this.onSearch(this.searchQuery);
      }
    });
  }

  private _toDossierRecord(dossier: AcepDossier): SearchRecord {
    return {
      id: dossier.refDossier || `${dossier.id}`,
      entityId: `${dossier.id}`,
      type: 'dossiers',
      title: dossier.refDossier || `Dossier ${dossier.id}`,
      subtitle: `${dossier.partnerName || 'N/A'} - ${dossier.status || 'N/A'}`
    };
  }

  private _toProjectRecord(
    project: RenovationProjectBeforeRenovation,
    parentDataNavigationId: string
  ): SearchRecord {
    return {
      id: project.projectReference || project.id,
      entityId: project.id,
      type: 'projects',
      title: project.projectReference || 'Projet sans référence',
      subtitle: `${project.partnerName || 'N/A'} - ${project.ceeProcessingStatusLabel || 'N/A'}`,
      parentDataNavigationId
    };
  }

  private _toLotCssRecord(batch: ExternalAuditBatch): SearchRecord {
    const lotId = (batch.batchReference || '').trim();
    return {
      id: lotId || batch.id,
      entityId: batch.id,
      type: 'lots-css',
      title: lotId || 'Lot CSS indirect',
      subtitle: batch.batchName || 'Liste des CSS indirects'
    };
  }

  private _toActorRecord(actor: Actor): SearchRecord {
    return {
      id: actor.code || actor.id,
      entityId: actor.id,
      type: 'actors',
      title: actor.name || 'Acteur',
      subtitle: `${actor.code || 'N/A'} - ${actor.actorType?.name || 'N/A'}`,
      actorTypeCode: actor.actorType?.code
    };
  }

  private _getActorContactNavigationId(actorTypeCode?: string): string {
    if (actorTypeCode === ActorTypes.Oblige) {
      return 'oblige_contacts';
    }

    if (actorTypeCode === ActorTypes.SousTraitant) {
      return 'soustraitant_contacts';
    }

    if (actorTypeCode === ActorTypes.Apporteur) {
      return 'apporteur_contacts';
    }

    if (actorTypeCode === ActorTypes.Mandataire) {
      return 'mandataire_contacts';
    }

    if (actorTypeCode === ActorTypes.Superviseur) {
      return 'superviseur_contacts';
    }

    return 'partenaire_contacts';
  }

  private _updateSearchPanelWidth(): void {
    const wrapElement = this.searchInputWrapRef?.nativeElement;
    if (!wrapElement) {
      return;
    }

    this.searchPanelWidth = `${wrapElement.offsetWidth}px`;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get browser's fullscreen element
   *
   * @private
   */
  private _getBrowserFullscreenElement(): Element {
    if (typeof this._fsDoc.fullscreenElement !== 'undefined') {
      return this._fsDoc.fullscreenElement;
    }

    if (typeof this._fsDoc.mozFullScreenElement !== 'undefined') {
      return this._fsDoc.mozFullScreenElement;
    }

    if (typeof this._fsDoc.msFullscreenElement !== 'undefined') {
      return this._fsDoc.msFullscreenElement;
    }

    if (typeof this._fsDoc.webkitFullscreenElement !== 'undefined') {
      return this._fsDoc.webkitFullscreenElement;
    }

    throw new Error('Fullscreen mode is not supported by this browser');
  }

  /**
   * Open the fullscreen
   *
   * @private
   */
  private _openFullscreen(): void {
    if (this._fsDocEl.requestFullscreen) {
      this._fsDocEl.requestFullscreen();
      return;
    }

    // Firefox
    if (this._fsDocEl.mozRequestFullScreen) {
      this._fsDocEl.mozRequestFullScreen();
      return;
    }

    // Chrome, Safari and Opera
    if (this._fsDocEl.webkitRequestFullscreen) {
      this._fsDocEl.webkitRequestFullscreen();
      return;
    }

    // IE/Edge
    if (this._fsDocEl.msRequestFullscreen) {
      this._fsDocEl.msRequestFullscreen();
      return;
    }
  }

  /**
   * Close the fullscreen
   *
   * @private
   */
  private _closeFullscreen(): void {
    if (this._fsDoc.exitFullscreen) {
      this._fsDoc.exitFullscreen();
      return;
    }

    // Firefox
    if (this._fsDoc.mozCancelFullScreen) {
      this._fsDoc.mozCancelFullScreen();
      return;
    }

    // Chrome, Safari and Opera
    if (this._fsDoc.webkitExitFullscreen) {
      this._fsDoc.webkitExitFullscreen();
      return;
    }

    // IE/Edge
    else if (this._fsDoc.msExitFullscreen) {
      this._fsDoc.msExitFullscreen();
      return;
    }
  }
}

type SearchRecordType = 'actors' | 'projects' | 'dossiers' | 'lots-css';

interface SearchResultGroup {
  id: SearchRecordType;
  results: SearchRecord[];
}

interface SearchRecord {
  id: string;
  entityId: string;
  type: SearchRecordType;
  title: string;
  subtitle: string;
  parentDataNavigationId?: string;
  actorTypeCode?: string;
}
